import React, { useState, useEffect } from 'react';
import { Camera, X, Maximize2, ChevronDown, AlertCircle, Loader2, Key, Download, Plus, LayoutGrid, Lock, ExternalLink } from 'lucide-react';
import { CreativeBrief, CampaignConcept, CampaignImage, AppStep } from './types';
import { generateCampaignSequence, generatePoseImage, setManualApiKey } from './services/geminiService';

declare global {
  var aistudio: {
    hasSelectedApiKey: () => Promise<boolean>;
    openSelectKey: () => Promise<void>;
  };
}

const POSE_OPTIONS = [
  "Dynamic Walking", "Power Stance", "Seated Elegance", "Lean & Relaxed", "Action Motion", 
  "Portrait Still", "Avant-Garde Contortion", "Candid Interaction", "Looking Away", "Striding Forward",
  "Adjusting Accessories", "Checking Smartwatch", "Gazing at Skyline", "Engaged in Tablet", "Leaning on Balcony",
  "Walking with Latte", "Shoulder Bag Swing", "Mid-Laughter Candid", "Running Up Stairs", "Relaxed Arm-Cross"
];

const ANGLE_OPTIONS = [
  "Eye Level", "Low Angle Hero", "High Angle Editorial", "Dutch Angle", "Extreme Close-up", 
  "Wide Landscape", "Top-down Bird's Eye", "Side Profile", "3/4 View", "Shoulder Level",
  "Worm's Eye View", "Overhead Geometric", "Ground-Level Perspective", "Deep Space Wide", "Extreme Telephoto",
  "High-Angle Plunge", "Low-Angle Monumental", "Symmetrical Center-Shot", "Point-of-View (POV)", "Cinematic Medium Shot"
];

const ASPECT_RATIO_OPTIONS = ["1:1", "3:4", "4:3", "9:16", "16:9"];
const RESOLUTION_OPTIONS = ["1K", "2K", "4K"];

const PhaseHeader: React.FC<{ phase: string; title: string }> = ({ phase, title }) => (
  <div className="flex flex-col items-center gap-2 mb-10 mt-16 text-center">
    <span className="text-[9px] font-bold uppercase tracking-[0.5em] text-[#dffe00]">Phase {phase}</span>
    <h2 className="text-xl md:text-2xl font-headline font-semibold tracking-[1px] text-white capitalize">{title}</h2>
    <div className="w-12 h-[1px] bg-[#dffe00]/30 mt-4" />
  </div>
);

const StudioDropdown: React.FC<{ label: string; value: string; options: string[]; onChange: (v: string) => void }> = ({ label, value, options, onChange }) => (
  <div className="flex flex-col gap-3 group">
    <label className="text-[8px] font-bold uppercase tracking-widest text-[#dffe00]/50">{label}</label>
    <div className="relative">
      <select 
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/[0.03] border border-white/10 rounded-xl py-4 px-5 text-[11px] text-white/80 appearance-none focus:outline-none focus:border-[#dffe00]/50 transition-all cursor-pointer hover:border-white/20"
      >
        {options.map((opt) => (
          <option key={opt} value={opt} className="bg-[#050505]">{opt}</option>
        ))}
      </select>
      <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-white/20"><ChevronDown className="w-4 h-4" /></div>
    </div>
  </div>
);

const App: React.FC = () => {
  const [step, setStep] = useState<AppStep>('landing');
  const [hasKey, setHasKey] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState("");

  const [brief, setBrief] = useState<CreativeBrief>({
    pose: POSE_OPTIONS[0],
    cameraAngle: ANGLE_OPTIONS[0],
    aspectRatio: "3:4",
    imageSize: "2K",
    anchorImage: null
  });

  const [images, setImages] = useState<CampaignImage[]>([]);

  useEffect(() => {
    if (typeof aistudio !== 'undefined') {
      aistudio.hasSelectedApiKey().then(setHasKey);
    }
  }, []);

  const handleKeySelection = async () => {
    if (inputValue.trim().length > 0) {
      setManualApiKey(inputValue.trim());
      setHasKey(true);
      return;
    }

    if (typeof aistudio !== 'undefined') {
      try {
        await aistudio.openSelectKey();
        setHasKey(true);
      } catch (e) {
        console.error("Error opening key selector:", e);
      }
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setBrief(p => ({ ...p, anchorImage: ev.target?.result as string }));
      reader.readAsDataURL(file);
    }
  };

  const handleDownloadAll = () => {
    images.forEach((img, index) => {
      if (img.status === 'completed' && img.imageUrl) {
        const link = document.createElement('a');
        link.href = img.imageUrl;
        link.download = `twin-studio-${img.id || index}.png`;
        document.body.appendChild(link);
        setTimeout(() => {
          link.click();
          document.body.removeChild(link);
        }, index * 300); // Slight delay for browser batch handling
      }
    });
  };

  const initiateProduction = async () => {
    if (!brief.anchorImage) return alert("Please upload your anchor image.");
    setIsGenerating(true);
    setImages([]);
    
    try {
      const concepts = await generateCampaignSequence(brief);
      const placeholderImages: CampaignImage[] = concepts.map(c => ({
        id: c.id,
        title: c.title,
        status: 'idle'
      }));
      setImages(placeholderImages);

      for (const concept of concepts) {
        setImages(prev => prev.map(img => img.id === concept.id ? { ...img, status: 'generating' } : img));
        try {
          const url = await generatePoseImage(brief, concept);
          setImages(prev => prev.map(img => img.id === concept.id ? { ...img, status: 'completed', imageUrl: url } : img));
        } catch (e: any) {
          if (e.message === "KEY_RESET_REQUIRED") {
            setHasKey(false);
            alert("Connection Lost. Please re-select your paid API key.");
            break;
          }
          setImages(prev => prev.map(img => img.id === concept.id ? { ...img, status: 'failed' } : img));
        }
      }
    } catch (e: any) {
      if (e.message === "KEY_RESET_REQUIRED") {
        setHasKey(false);
        alert("Connection Lost. Please re-select your paid API key.");
      } else {
        alert("Production error. Check console for details.");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  if (!hasKey) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6 text-white text-center">
        <div className="max-w-md w-full flex flex-col items-center animate-fade-in">
          
          {/* Lock Icon */}
          <div className="w-16 h-16 rounded-[2rem] border border-white/10 bg-white/5 flex items-center justify-center mb-8">
            <Lock className="w-6 h-6 text-white/50" />
          </div>

          {/* Title */}
          <h1 className="font-headline font-semibold tracking-tight text-3xl md:text-4xl mb-3 text-white">
            AI Twin Studio: <span className="text-[#dffe00]">Campaign</span>
          </h1>

          {/* Subtitle */}
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/30 mb-12">
            Connect Studio Access Key To Begin
          </p>

          {/* Input Field */}
          <div className="w-full max-w-[320px] relative mb-4 group">
            <input 
              type="password"
              placeholder="Connect Gemini API Key..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleKeySelection()}
              className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg py-3 pl-4 pr-10 text-[11px] text-white font-mono placeholder:text-white/30 focus:outline-none focus:border-[#dffe00]/50 focus:bg-white/[0.02] transition-all"
            />
            <div className="absolute left-0 top-3 h-4 w-[2px] bg-[#dffe00] animate-pulse pointer-events-none opacity-0 group-focus-within:opacity-0 transition-opacity" />
            {inputValue.length === 0 && (
                 <div className="absolute left-0 top-3 h-4 w-[2px] bg-[#dffe00] animate-pulse pointer-events-none ml-4" />
            )}
          </div>

          {/* Button */}
          <button 
            onClick={handleKeySelection} 
            className="w-full max-w-[320px] py-4 bg-white text-black text-[11px] font-bold uppercase tracking-[0.2em] rounded-lg hover:bg-[#dffe00] transition-colors mb-12"
          >
            Activate Studio
          </button>

          {/* Divider */}
          <div className="w-full max-w-[120px] h-px bg-white/10 mb-8" />

          {/* Footer */}
          <a 
            href="https://ai.google.dev/gemini-api/docs/billing" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest text-white/30 hover:text-white transition-colors"
          >
            Get your free API key from Google
            <ExternalLink className="w-3 h-3" />
          </a>

        </div>
      </div>
    );
  }

  if (step === 'landing') {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent z-10" />
        <div className="relative z-20 text-center animate-fade-in px-6">
          <h1 className="text-4xl md:text-6xl font-headline font-semibold tracking-[1px] text-white mb-6 capitalize">AI Twin Studio: <span className="text-[#dffe00]">Campaign</span></h1>
          <p className="text-white/40 uppercase tracking-[0.5em] text-[9px] mb-12 max-w-lg mx-auto leading-relaxed">Transform one photo into a sequence of 8 high-fashion poses while maintaining the exact outfit and location.</p>
          <button onClick={() => setStep('setup')} className="px-16 py-6 border border-white/10 rounded-full text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white hover:text-black transition-all">Launch Studio</button>
        </div>

        <div className="absolute bottom-8 left-0 w-full flex justify-center z-30">
            <button onClick={() => { setHasKey(false); setInputValue(""); }} className="text-[9px] font-bold uppercase tracking-widest text-white/30 hover:text-white transition-colors flex items-center gap-2">
                <Key className="w-3 h-3" />
                Update API Key
            </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-[#dffe00] selection:text-black pb-32">
      <nav className="fixed top-0 left-0 w-full z-50 px-8 py-6 flex justify-between items-center bg-black/50 backdrop-blur-lg border-b border-white/5">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => setStep('landing')}>
          <span className="font-headline font-semibold tracking-[1px] text-[12px] capitalize">AI Twin Studio: Campaign</span>
        </div>
        <div className="flex gap-6">
           <button className="text-[9px] font-bold uppercase tracking-widest text-white/40 hover:text-white" onClick={() => setStep('landing')}>Exit</button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 pt-32">
        <section className="flex flex-col items-center">
          <PhaseHeader phase="01" title="Identity Anchor" />
          <div className="w-full max-w-sm">
            <div className="relative aspect-[3/4] bg-white/[0.02] border border-white/10 rounded-3xl overflow-hidden group transition-all hover:border-[#dffe00]/50">
              {brief.anchorImage ? (
                <>
                  <img src={brief.anchorImage} className="w-full h-full object-cover" alt="Anchor" />
                  <button onClick={() => setBrief(p => ({ ...p, anchorImage: null }))} className="absolute top-4 right-4 p-3 bg-black/60 rounded-full backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-4 h-4" /></button>
                </>
              ) : (
                <label className="w-full h-full flex flex-col items-center justify-center gap-4 cursor-pointer text-white/10 hover:text-[#dffe00]/50 transition-colors">
                  <div className="p-6 rounded-full border border-dashed border-current"><Plus className="w-8 h-8" /></div>
                  <span className="text-[9px] uppercase tracking-widest font-bold">Upload Portrait</span>
                  <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} />
                </label>
              )}
            </div>
          </div>
        </section>

        <section className="flex flex-col items-center">
          <PhaseHeader phase="02" title="Creative Sequence" />
          <div className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-4 gap-6">
            <StudioDropdown label="Dominant Pose Style" value={brief.pose} options={POSE_OPTIONS} onChange={v => setBrief(p => ({ ...p, pose: v }))} />
            <StudioDropdown label="Primary Camera Angle" value={brief.cameraAngle} options={ANGLE_OPTIONS} onChange={v => setBrief(p => ({ ...p, cameraAngle: v }))} />
            <StudioDropdown label="Aspect Ratio" value={brief.aspectRatio} options={ASPECT_RATIO_OPTIONS} onChange={v => setBrief(p => ({ ...p, aspectRatio: v as any }))} />
            <StudioDropdown label="Resolution" value={brief.imageSize} options={RESOLUTION_OPTIONS} onChange={v => setBrief(p => ({ ...p, imageSize: v as any }))} />
          </div>
          
          <div className="mt-16 w-full flex flex-col items-center">
             <button 
               onClick={initiateProduction} 
               disabled={isGenerating || !brief.anchorImage} 
               className="px-20 py-8 bg-[#dffe00] text-black text-[10px] font-bold uppercase tracking-[0.4em] rounded-full hover:bg-white hover:scale-105 transition-all duration-500 disabled:opacity-20 shadow-[0_20px_50px_rgba(223,254,0,0.2)]"
             >
               {isGenerating ? "Synthesizing Campaign..." : "Generate 8 Shots"}
             </button>
          </div>
        </section>

        {images.length > 0 && (
          <section className="flex flex-col items-center pt-24 animate-fade-in w-full">
             <div className="flex flex-col md:flex-row items-center justify-between w-full mb-16 gap-8">
               <div className="flex items-center gap-4">
                 <LayoutGrid className="w-5 h-5 text-[#dffe00]" />
                 <h2 className="text-xl font-headline font-semibold tracking-[1px] text-white capitalize">Campaign Sequence</h2>
               </div>
               {images.some(img => img.status === 'completed') && (
                 <button 
                   onClick={handleDownloadAll}
                   className="flex items-center gap-2 px-8 py-4 bg-white/5 border border-white/10 rounded-full text-[9px] font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-all"
                 >
                   <Download className="w-4 h-4" />
                   Download All
                 </button>
               )}
             </div>
             
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-16 w-full pb-32">
                {images.map(img => (
                  <div key={img.id} className="flex flex-col gap-4">
                    <div className="relative w-full aspect-[3/4] bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden group hover:shadow-[0_20px_60px_rgba(0,0,0,0.8)] transition-all duration-500 hover:-translate-y-2 z-0 hover:z-10">
                      {img.status === 'completed' ? (
                        <>
                          <img 
                            src={img.imageUrl} 
                            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" 
                            alt={img.title} 
                          />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-4 backdrop-blur-sm">
                            <button onClick={() => setPreviewImage(img.imageUrl!)} className="p-4 bg-white text-black rounded-full hover:scale-110 active:scale-95 transition-transform"><Maximize2 className="w-5 h-5" /></button>
                            <button onClick={() => { const link = document.createElement('a'); link.href = img.imageUrl!; link.download = `twin-${img.id}.png`; link.click(); }} className="p-4 bg-[#dffe00] text-black rounded-full hover:scale-110 active:scale-95 transition-transform"><Download className="w-5 h-5" /></button>
                          </div>
                        </>
                      ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                           {img.status === 'failed' ? (
                             <AlertCircle className="w-5 h-5 text-red-500/50" />
                           ) : (
                             <Loader2 className={`w-8 h-8 text-[#dffe00]/30 ${img.status === 'generating' ? 'animate-spin text-[#dffe00]' : ''}`} />
                           )}
                           <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-white/30">
                             {img.status === 'generating' ? 'Synthesizing' : img.status === 'failed' ? 'Failed' : 'Queued'}
                           </span>
                        </div>
                      )}
                    </div>
                    <div className="px-1">
                      <p className="text-[8px] font-bold uppercase tracking-[0.2em] text-[#dffe00] truncate mb-1">{img.title}</p>
                      <div className="w-6 h-[1px] bg-white/10" />
                    </div>
                  </div>
                ))}
             </div>
          </section>
        )}
      </main>
      
      <footer className="w-full py-12 flex justify-center border-t border-white/5 mt-24">
          <button onClick={() => { setHasKey(false); setInputValue(""); }} className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest text-white/20 hover:text-white transition-colors">
             <Key className="w-3 h-3" />
             Update API Key
          </button>
      </footer>

      {previewImage && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-8 backdrop-blur-xl" onClick={() => setPreviewImage(null)}>
          <img src={previewImage} className="max-w-full max-h-full object-contain rounded-xl shadow-2xl border border-white/10" alt="Preview" />
          <button className="absolute top-8 right-8 text-white/40 hover:text-white transition-colors"><X className="w-8 h-8" /></button>
        </div>
      )}
    </div>
  );
};

export default App;